import Vue from "vue";
import { reactive } from "vue";
import { Component, Watch } from 'vue-property-decorator';
import { ui } from '@/constants';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { OrderConfirmedEvent, CheckoutEvent, ErrorEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgOrderReviewModal',
})
export default class CmgOrderReviewModal extends Vue {
  interval: NodeJS.Timeout | undefined;
  timer = reactive({
    value: 10
  })

  get visible(): boolean {
    return this.$appStore.state.ui.modals.orderReviewModal.visible;
  }

  get args() {
    return this.$appStore.state.ui.modals.orderReviewModal.args;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get modalTitle(): string {
    return this.$services.config.getOrderReviewModal()?.title || '';
  }

  get primaryCta(): string {
    return `${this.$services.config.getOrderReviewModal()?.primaryCta}   0:${this.timer.value < 10 ? '0' : ''}${this.timer.value}`;
  }

  get secondaryCta(): string {
    return this.$services.config.getOrderReviewModal()?.secondaryCta || '';
  }

  get orderLocationText(): string {
    return this.args?.isPickupOrder ? "Pickup Location" : "Delivery Address";
  }

  get locationIconUrl(): string { 
    return this.$appStore.state.cms.images.icons.interstitialLocation.default;
  }

  get timeIconUrl(): string {
    return this.$appStore.state.cms.images.icons.interstitialTime.default;
  }

  get addressNickname(): string | undefined {
    if (this.args?.isPickupOrder && this.args?.restaurant?.restaurantNumber) {
      return this.$services.customer.getFavoriteRestaurantName(this.args.restaurant.restaurantNumber);
    } else {
      return this.$appStore.state.order.selectedDeliveryAddress?.name;
    }
  }

  get addressLine1(): string {
    return this.args!.address!.line1
  }

  get addressLine2(): string {
    return `${this.args?.address?.locality}, ${this.args?.address?.administrativeArea} ${this.args?.address?.postalCode?.split("-")[0]}`;
  }

  get addressSubtext(): string {
    if (this.args?.isPickupOrder) {
      const restaurantDirections = this.args.restaurant?.directions;
      return `${restaurantDirections?.crossStreet1} & ${restaurantDirections?.crossStreet2}. ${restaurantDirections?.landmark ? restaurantDirections?.landmark : ''}`;
    } else {
      const deliveryAddress = this.$appStore.state.order.selectedDeliveryAddress;
      return `Instructions: ${deliveryAddress?.contactlessDropoff ? 'Leave it at my door' : 'Hand it to me'}`;
    }
  }

  private getCountdownTimerValue() {
    return this.$services.config.getOrderReviewModal()?.countdownTimer || 10;
  }

  private getOrderReviewTime() {
    return this.getCountdownTimerValue() - this.timer.value;
  }

  get orderReadyText(): string {
    return this.args?.isPickupOrder ? "Pickup Time" : "Estimated Arrival";
  }

  get orderReadyTime(): string {
    const checkout = this.$appStore.state.checkout;
    if (this.args?.isPickupOrder) {
      const pickupTime = new Date(checkout.pickup.pickupTime.value).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      return pickupTime;
    } else {
      return this.formatArrivalTimeEstimate(checkout.delivery.estimate?.minDeliveryTime, checkout.delivery.estimate?.maxDeliveryTime);  
    }
  }

  @Watch('visible', { immediate: false })
  onVisibleChanged(newVisible: boolean) {
    if (newVisible) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          clearInterval(this.interval);
          this.$services.checkout.setIsOrderConfirmed(false);
        }
      }, { once: true });

      window.addEventListener('beforeunload', this.handleInterrupt, { once: true });

      this.timer.value = this.args!.countdownTimerValue;
      this.interval = setInterval(() => {
        if (this.timer.value > 0) {
          this.timer.value--;
          this.$services.ui.setOrderReviewTime(this.getOrderReviewTime());
        } else {
          this.onClickLooksGood('automatic-timeout');
        }
      }, 1000);
    } else {
      window.removeEventListener('beforeunload', this.handleInterrupt);
    }
  }

  mounted() {
    this.$router.beforeEach((to, from, next) => { 
      if (this.visible) {
        clearInterval(this.interval);
        this.$services.ui.setModalVisibility('orderReviewModal', false);
        this.$services.checkout.setIsOrderConfirmedError(true);

        this.$services.ui.setModalVisibility('basicError', true, {
          header: "AW SHUCKS",
          content: `Your order wasn\'t submitted.\n Please try again. <video autoplay loop muted class="maxw-100" src="${this.$appStore.state.cms.images.feedback.sadAvocado.default}"/>`,
          background: "white",
          isInterstitialError: true,
          options: [
            {
              text: 'BACK TO CHECKOUT',
              type: ButtonType.Primary,
              onClick: () => {
                this.$services.ui.setModalVisibility('toast', false);
                this.$services.ui.setModalVisibility('basicError', false);
              },
            },
          ],
          onClose: () => { this.$services.ui.setModalVisibility('toast', false); }
        });

        // Send analytics call
        this.$services.analytics.applyOne({ 
          commerce: { 
            orderNotSubmitted: { "value": 1 }
          },
          web: {
            webPageDetails: {
              "name": "order:checkout:cancel-order-review"
            }
          }
        });
        this.$services.analytics.send(new ErrorEvent('checkout', `AW SHUCKS. Your order wasn't submitted. Please try again.`, 'browser-back-btn-on-order-review'));

        this.$services.checkout.setIsOrderConfirmed(false);
        next(false);
      } else {
        next();
      }
    })
  }

  onClickLooksGood(orderConfirmationMethod: string) {
    this.$services.ui.setOrderCancellation(this.getOrderReviewTime(), orderConfirmationMethod);
    this.$services.analytics.send(new OrderConfirmedEvent('LOOKS GOOD'));
    clearInterval(this.interval);
    this.$services.checkout.setIsOrderConfirmed(true);

    this.args!.countdownTimerValue = this.getCountdownTimerValue();
    this.$services.ui.setModalVisibility('orderReviewModal', false);
  }

  onClickCancel() {
    this.$services.analytics.applyOne({  
      web: {
        webPageDetails: {},
        webInteraction: {
          "linkClicks": { "value": 1 },
          "name": "CANCEL ORDER",
          "type": "other"
        }
      }    
    });
    this.$services.analytics.send(new CheckoutEvent('cancel-order-review'));
    clearInterval(this.interval);
    this.$services.checkout.setIsOrderConfirmed(false);

    this.args!.countdownTimerValue = this.getCountdownTimerValue();
    this.$services.ui.setModalVisibility('orderReviewModal', false);
  }

  private formatArrivalTimeEstimate(minTime: number | undefined, maxTime: number | undefined): string {
    const currentTime = new Date();
    if (minTime === undefined || maxTime === undefined) {
      return '';
    }
    
    const minArrivalTime = new Date(currentTime.getTime());
    minArrivalTime.setMinutes(minArrivalTime.getMinutes() + minTime);

    const formattedMinTime = minArrivalTime.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const maxArrivalTime = new Date(currentTime.getTime());
    maxArrivalTime.setMinutes(maxArrivalTime.getMinutes() + maxTime);

    const formattedMaxTime = maxArrivalTime.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${formattedMinTime} - ${formattedMaxTime}`;
  }

  // in case user refreshes the page midway through countdown
  private handleInterrupt() {
    if (this.$appStore.state.checkout.isOrderConfirmed === null) {
      this.$services.checkout.setIsOrderConfirmedError(true);
    }
  }
}
